import { ChangeDetectionStrategy, Component, input, model, output, signal } from '@angular/core';
import { DocumentiSearchDocRow } from 'src/app/shared/models/documento-search.model';
import { OperazioneMassivaRegistro, OperazioneMassivaResponse, OperazioneMassivaTipo } from '../../models/operazioni-massive-service.model';
import { HeaderDeclassificaOperazioniMassive, HeaderDistruzioneOperazioniMassive, HeaderVersamentoOperazioniMassive, SummaryDocumentoToDestroy } from '../../models/operazioni-massive.model';

export type OperazioneMassivaTipoSeverity = 'danger' | 'warning' | 'info' | 'secondary' | undefined;

@Component({
  selector: 'app-operazioni-massive-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-table [value]="data()" [totalRecords]="data().length" [paginator]="true" [rows]="10" [rowsPerPageOptions]="[10, 20, 30]">
      <ng-template pTemplate="header">
        <tr>
          <th id="dataDocumento" style="width:'90px';">Data</th>
          <th id="tipo" style="width:'180px';">Tipo</th>
          <th id="numeroVerbale" style="width:'90px';">Numero verbale</th>
          <th id="numeroOrdine" style="width:'90px';">Numero ordine registro</th>
          <th id="classifica" style="width:'180px';">Classifica/Qualifica</th>
          <th id="numDocumenti" style="width:'90px';">Numero documenti</th>
          <th id="action-buttons"></th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-rowData>
        <tr>
          <td>{{rowData['dataOperazione'] | date: 'dd/MM/yyyy'}}</td>
          <td><p-badge [value]="decodeTipo(rowData['tipo'])" badgeSize="large" [severity]="decodeSevertyTipo(rowData)" /></td>
          <td>{{rowData['numeroVerbale']}}</td>
          <td>{{ getRegisterInfo( 'numeroOrdine',rowData)}}</td>
          <td>{{ getRegisterInfo( 'classificaQualifica',rowData)}}</td>
          <td>{{rowData['numDocumenti']}}</td>

          <td class="action-buttons">
              <button pButton type="button" title="Anteprima" icon="fas fa-search" (click)="viewAnteprima(rowData) "></button>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan=6>
            Nessun documento selezionato
          </td>
        </tr>
      </ng-template>
    </p-table>

    <app-verbale-distruzione-dialog [(isOpen)]="isPreviewDistruzioneOpen" [id]="operazioneId()" [protocol]="currentProtocol()" [header]="distruzioneCurrentHeader()" [documents]="distruzioneCurrentDocuments()" />
    <app-riepilogo-declassifica-dialog [(isOpen)]="isPreviewDeclassificaOpen" [id]="operazioneId()" [header]="declassificaCurrentHeader()" [documents]="declassificaCurrentDocuments()"/>
    <app-riepilogo-versamento-dialog [(isOpen)]="isPreviewVersamentoOpen" [id]="operazioneId()" [header]="versamentoCurrentHeader()" [documents]="versamentoCurrentDocuments()"/>
  `,
  styles: [
    `
      :host ::ng-deep td {
        padding: 0.25em 0.5em !important;
        text-align: center;
      }

      .action-buttons {
        display: flex;
        gap: 5px;
      }
    `
  ]
})
export class OperazioniMassiveListComponent {
  data = input<OperazioneMassivaResponse[]>([]);
  unselected = output<OperazioneMassivaResponse>();

  public isPreviewDistruzioneOpen = model<boolean>(false);
  public isPreviewDeclassificaOpen = model<boolean>(false);
  public isPreviewVersamentoOpen = model<boolean>(false);
  public currentProtocol = signal<string>('');
  public operazioneId = signal<number|undefined>(undefined);
  
  public distruzioneCurrentHeader = signal<HeaderDistruzioneOperazioniMassive>({} as HeaderDistruzioneOperazioniMassive);
  public distruzioneCurrentDocuments = signal<SummaryDocumentoToDestroy[]>([]);
  public declassificaCurrentHeader = signal<HeaderDeclassificaOperazioniMassive | undefined>(undefined);
  public declassificaCurrentDocuments = signal<DocumentiSearchDocRow[]>([]);
  public versamentoCurrentHeader = signal<HeaderVersamentoOperazioniMassive | undefined>(undefined);
  public versamentoCurrentDocuments = signal<DocumentiSearchDocRow[]>([]);


  constructor() { }

  public decodeSevertyTipo(item: OperazioneMassivaResponse): OperazioneMassivaTipoSeverity {

    let result: OperazioneMassivaTipoSeverity = undefined;
    if (item.tipo == OperazioneMassivaTipo.Distruzione && item.registro)
      result = 'danger';
    if (item.tipo == OperazioneMassivaTipo.Distruzione && !item.registro)
      result = 'secondary';
    if (item.tipo == OperazioneMassivaTipo.Declassifica)
      result = 'warning';
    if (item.tipo == OperazioneMassivaTipo.Versamento)
      result = 'info';

    return result;
  }

  public decodeTipo(tipo: OperazioneMassivaTipo): string | undefined {
    const result: string | undefined = OperazioneMassivaTipo[tipo];
    return result;
  }

  public getRegisterInfo(property: keyof OperazioneMassivaRegistro, item: OperazioneMassivaResponse): string | Date | number | undefined {

    if (item?.registro) {
      return item.registro[property];
    }
    return undefined;
  }

  public viewAnteprima(item: OperazioneMassivaResponse) {

    this.operazioneId.set(item.operazioneId);

    if (item.tipo === OperazioneMassivaTipo.Distruzione) {
      const data: { header: HeaderDistruzioneOperazioniMassive, documents: SummaryDocumentoToDestroy[] } = JSON.parse(item.dati);
      if (data) {
        this.currentProtocol.set(`${item.registro?.numeroOrdine}`);
        this.distruzioneCurrentHeader.set(data.header);
        this.distruzioneCurrentDocuments.set(data.documents);
        this.isPreviewDistruzioneOpen.set(true);
      }
    }

    if (item.tipo === OperazioneMassivaTipo.Declassifica) {
      const data: { header: HeaderDeclassificaOperazioniMassive, documents: DocumentiSearchDocRow[] } = JSON.parse(item.dati);

      if (data) {
        this.declassificaCurrentHeader.set(data.header);
        this.declassificaCurrentDocuments.set(data.documents);
        this.isPreviewDeclassificaOpen.set(true);
      }
    }

    if (item.tipo === OperazioneMassivaTipo.Versamento) {
      const data: { header: HeaderVersamentoOperazioniMassive, documents: DocumentiSearchDocRow[] } = JSON.parse(item.dati);

      if (data) {
        this.versamentoCurrentHeader.set(data.header);
        this.versamentoCurrentDocuments.set(data.documents);
        this.isPreviewVersamentoOpen.set(true);
      }
    }
  }
}
